import clsx from "clsx";
import { type KeenSliderOptions, useKeenSlider } from "keen-slider/react";

import "keen-slider/keen-slider.min.css";

import { useLocaleInfo } from "@/hooks/useLocaleInfo";

export type ImageCarouselProps<ItemShape> = {
  items: ItemShape[];
  renderItem: (item: ItemShape, index: number) => React.ReactNode;
  getItemKey?: (item: ItemShape, index: number) => string | number;
  slideClassName?: string;
  className?: string;
} & Partial<KeenSliderOptions>;

export function ImageCarousel<ItemShape>(props: ImageCarouselProps<ItemShape>) {
  const {
    items,
    renderItem,
    getItemKey,
    slideClassName,
    className,
    loop = true,
    renderMode = "performance",
    slides,
    disabled = items.length === 0,
    ...restSliderOptions
  } = props;
  const { dir } = useLocaleInfo();

  const renderItems = loop && items.length === 2 ? [...items, ...items] : items;

  const [containerRef] = useKeenSlider<HTMLDivElement>({
    loop,
    renderMode,
    rtl: dir === "rtl",
    disabled,
    ...restSliderOptions,
    slides:
      typeof slides === "object"
        ? {
            perView: "auto",
            spacing: 8,
            origin: "center",
            number: renderItems.length,
            ...slides,
          }
        : slides,
  });

  if (items.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={clsx("keen-slider", className)}
      data-testid="carousel"
    >
      {renderItems.map((item, index) => (
        <div
          key={getItemKey ? getItemKey(item, index) : index}
          className={clsx("keen-slider__slide", slideClassName)}
          data-testid="carousel-item"
        >
          {renderItem(item, index)}
        </div>
      ))}
    </div>
  );
}
